// Dashboard-progress response DTOs — consumed by both frontend and backend.
//
// These types define the wire contract for GET /admin/dashboard/progress.
// Keeping them in @citycare/shared guarantees the two sides can never drift.

// Encore's parser does not support `(typeof X)[number]` indexed-access
// types, so the union is written out explicitly. PROGRESS_DEPTHS still
// ships as the runtime list — keep them in sync.
export type ProgressDepth = 'base' | 'site' | 'building'
export const PROGRESS_DEPTHS: readonly ProgressDepth[] = [
  'base',
  'site',
  'building',
]

export function isProgressDepth(value: unknown): value is ProgressDepth {
  return (
    typeof value === 'string' &&
    (PROGRESS_DEPTHS as readonly string[]).includes(value)
  )
}

export interface ProgressNode {
  id: string | null
  name: string
  parentId: string | null
  notStarted: number
  inProgress: number
  complete: number
  // Informational counts for the two admin-only statuses. They are
  // reported alongside the capture-status buckets but are deliberately
  // EXCLUDED from `total` / `completionPercent` — the completion formula
  // (denominator = notStarted + inProgress + complete) is unchanged.
  toBeDemolished: number
  outOfScope: number
  total: number
  completionPercent: number
  // Number of capture_items rows recorded against this node's scope
  // (a base / site / building / the dashboard summary). A row counts
  // as captured when its quantity > 0 OR is_zero_confirmed = true —
  // the same definition used across the dashboard and CSV export.
  assetsCaptured: number
}

export interface ProgressDashboardParams {
  depth?: string
  baseId?: string
  siteId?: string
  cursor?: string
  limit?: string
}

export interface ProgressDashboardResponse {
  depth: ProgressDepth
  nodes: ProgressNode[]
  summary: ProgressNode
}
